import { useCallback, useEffect, useState } from 'react';

import { fetchSetupState, type SetupConfirmation, type SetupState } from '../../core/setupStateApi';
import { fetchMatches, type MatchesState } from '../Data/matchesApi';
import { fetchPlansStatus, usePlansStatus, type PlansStatus } from '../Plans/plansGenerated';
import { fetchDataStatus, type PeDataStatus } from '../ProgramEvaluation/dataStatus';
import { programEvaluationCardView } from '../Reports/programEvaluationCard';
import {
  fetchProgramEvaluationStatus,
  useProgramEvaluationStatus,
  type PeStatus,
  type PeStatusSource,
} from '../Reports/programEvaluationStatus';

export type SetupFacts = {
  sisRequestedAt: string | null;
  sisConnected: boolean;
  sisConnectedAt: string | null;
  accessUploaded: boolean;
  schoolYear: string | null;
  exitCriteriaConfirmed: SetupConfirmation | null;
  yearsConfirmed: SetupConfirmation | null;
  plansGenerated: boolean;
  plansGeneratedAt: string | null;
  sisMatchingConfirmedAt: string | null;
  sisMatchingRequired: boolean;
  programEvaluation: PeStatus | null;
};

export type SetupStatusSources = {
  dataStatus: () => Promise<PeDataStatus>;
  setupState: () => Promise<SetupState>;
  plansStatus: () => Promise<PlansStatus>;
  peStatus: PeStatusSource;
};

export const serverSetupStatusSources: SetupStatusSources = {
  dataStatus: fetchDataStatus,
  setupState: fetchSetupState,
  plansStatus: fetchPlansStatus,
  peStatus: fetchProgramEvaluationStatus,
};

export type SetupStatusPhase = 'pending' | 'ready' | 'failed';

export type SetupStatus = {
  phase: SetupStatusPhase;
  facts: SetupFacts | null;
  programEvaluationGivenUp: boolean;
  refetch: () => void;
  refetchProgramEvaluation: () => void;
};

export function sisMatchingDone(facts: Pick<SetupFacts, 'sisMatchingConfirmedAt' | 'sisMatchingRequired'>): boolean {
  return !facts.sisMatchingRequired || facts.sisMatchingConfirmedAt !== null;
}

export function programEvaluationReady(
  facts: Pick<SetupFacts, 'sisConnected' | 'accessUploaded' | 'sisMatchingConfirmedAt' | 'sisMatchingRequired'>,
): boolean {
  return facts.sisConnected && facts.accessUploaded && sisMatchingDone(facts);
}

const IDLE_GENERATION = { phase: 'idle', jobId: null, jobUpdatedAt: null, pollExhausted: false } as const;

export function programEvaluationGenerated(status: PeStatus | null, dataYear: string | null): boolean {
  const view = programEvaluationCardView('present', status, IDLE_GENERATION, false, dataYear);
  return view.kind === 'generated' || view.kind === 'stale';
}

export function setupDone(facts: SetupFacts): boolean {
  return (
    facts.sisConnected &&
    facts.accessUploaded &&
    facts.exitCriteriaConfirmed !== null &&
    facts.yearsConfirmed !== null &&
    sisMatchingDone(facts) &&
    facts.plansGenerated &&
    programEvaluationGenerated(facts.programEvaluation, facts.schoolYear)
  );
}

type Read<T> = { kind: 'pending' } | { kind: 'done'; value: T } | { kind: 'failed' };

const PENDING: Read<never> = { kind: 'pending' };

function useRead<T>(enabled: boolean, readKey: string, read: () => Promise<T>, label: string): Read<T> {
  const [settled, setSettled] = useState<{ readKey: string; read: Read<T> } | null>(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    read()
      .then((value) => {
        if (active) setSettled({ readKey, read: { kind: 'done', value } });
      })
      .catch((err: unknown) => {
        console.warn(`setup status: ${label} unavailable`, err);
        if (active) setSettled({ readKey, read: { kind: 'failed' } });
      });
    return () => {
      active = false;
    };
  }, [enabled, readKey, read, label]);
  if (!enabled || settled === null || settled.readKey !== readKey) return PENDING;
  return settled.read;
}

function useFreshEvaluation(
  source: PeStatusSource,
  enabled: boolean,
  readKey: string,
): { status: PeStatus | null; givenUp: boolean; refetch: () => void } {
  const freshSource = useCallback<PeStatusSource>(() => source().then((status) => ({ ...status })), [source]);
  const evaluation = useProgramEvaluationStatus(freshSource, enabled);
  const [seen, setSeen] = useState<{ readKey: string; stale: PeStatus | null } | null>(null);
  if (enabled && (seen === null || seen.readKey !== readKey)) {
    if (seen !== null) evaluation.refetch();
    setSeen({ readKey, stale: evaluation.status });
    return { status: null, givenUp: false, refetch: evaluation.refetch };
  }
  const fresh = seen !== null && evaluation.status !== seen.stale;
  return { status: fresh ? evaluation.status : null, givenUp: evaluation.givenUp, refetch: evaluation.refetch };
}

export function useSetupStatus(
  enabled: boolean,
  sources: SetupStatusSources = serverSetupStatusSources,
  sessionKey = '',
): SetupStatus {
  const [attempt, setAttempt] = useState(0);
  const readKey = `${sessionKey}#${attempt}`;
  const data = useRead(enabled, readKey, sources.dataStatus, 'data status');
  const setup = useRead(enabled, readKey, sources.setupState, 'setup state');
  const plansRead = useRead(enabled, readKey, sources.plansStatus, 'plans status');
  const livePlans = usePlansStatus(enabled);
  const allPresent = data.kind === 'done' && programEvaluationReady({
    sisConnected: data.value.rosterStreamConnected,
    accessUploaded: data.value.accessPresent,
    sisMatchingConfirmedAt: data.value.sisMatchingConfirmedAt,
    sisMatchingRequired: data.value.sisMatchingRequired,
  });
  const evaluation = useFreshEvaluation(sources.peStatus, enabled && allPresent, readKey);
  const refetch = useCallback(() => setAttempt((n) => n + 1), []);

  const failed = data.kind === 'failed' || setup.kind === 'failed' || plansRead.kind === 'failed' || (allPresent && evaluation.givenUp);
  if (failed) {
    return {
      phase: 'failed',
      facts: null,
      programEvaluationGivenUp: evaluation.givenUp,
      refetch,
      refetchProgramEvaluation: evaluation.refetch,
    };
  }
  if (data.kind !== 'done' || setup.kind !== 'done' || plansRead.kind !== 'done' || (allPresent && evaluation.status === null)) {
    return {
      phase: 'pending',
      facts: null,
      programEvaluationGivenUp: false,
      refetch,
      refetchProgramEvaluation: evaluation.refetch,
    };
  }
  const plans = livePlans ?? plansRead.value;
  return {
    phase: 'ready',
    facts: {
      sisRequestedAt: setup.value.sisRequestedAt,
      sisConnected: data.value.rosterStreamConnected,
      sisConnectedAt: data.value.rosterStreamConnected ? data.value.rosterStreamConnectedAt : null,
      accessUploaded: data.value.accessPresent,
      schoolYear: data.value.schoolYear,
      exitCriteriaConfirmed: setup.value.exitCriteriaConfirmed,
      yearsConfirmed: setup.value.yearsConfirmed,
      plansGenerated: plans.generated,
      plansGeneratedAt: plans.generated ? plans.generatedAt : null,
      sisMatchingConfirmedAt: data.value.sisMatchingConfirmedAt,
      sisMatchingRequired: data.value.sisMatchingRequired,
      programEvaluation: allPresent ? evaluation.status : null,
    },
    programEvaluationGivenUp: false,
    refetch,
    refetchProgramEvaluation: evaluation.refetch,
  };
}

export type MatchesSummary = { phase: SetupStatusPhase; state: MatchesState | null };

export function useMatchesSummary(enabled: boolean, sessionKey = ''): MatchesSummary {
  const read = useRead(enabled, sessionKey, fetchMatches, 'matches');
  if (read.kind === 'done') return { phase: 'ready', state: read.value };
  return { phase: read.kind === 'failed' ? 'failed' : 'pending', state: null };
}
